"use client";

import { stripStega } from "@datocms/content-link";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { useBoolean, useIntersection } from "react-use";
import {
	BLOG_HEADER_ITEM_ID,
	SUCCESS_STORIES_HEADER_ITEM_ID,
} from "../lib/cms-constants";
import type {
	HeaderFieldsFragment,
	SiteLocale,
} from "src/__generated__/graphql";

/**
 * use an intersection observer to toggle a bool from false to true ONCE when visible
 * @param threshold intersection observer threshold
 * @param delay delay execution
 */
export const useOneOffWhenVisible = (
	threshold = 0.1,
	delay = 0,
): [RefObject<HTMLDivElement>, boolean] => {
	const [oneOff, toggleOneOff] = useBoolean(false);
	const intersectionRef = useRef(null);
	const intersection = useIntersection(intersectionRef, {
		root: null,
		rootMargin: "0px",
		threshold,
	});

	useEffect(() => {
		if (!intersection || intersection.intersectionRatio < threshold) {
			return () => {};
		}

		const timeout = setTimeout(() => toggleOneOff(true), delay);

		return () => clearTimeout(timeout);
	}, [delay, intersection, threshold, toggleOneOff]);

	return [intersectionRef, oneOff];
};

/**
 * returns a randomly shuffled copy of an array
 * @param arr array of items
 */
export const shuffleArray = <T>(arr: Array<T>) =>
	arr.sort(() => Math.random() - 0.5);

export const monthsFullName: { [key: string]: { [key: number]: string } } = {
	en: {
		0: "January",
		1: "February",
		2: "March",
		3: "April",
		4: "May",
		5: "June",
		6: "July",
		7: "August",
		8: "September",
		9: "October",
		10: "November",
		11: "December",
	},
	de: {
		0: "Januar",
		1: "Februar",
		2: "März",
		3: "April",
		4: "Mai",
		5: "Juni",
		6: "Juli",
		7: "August",
		8: "September",
		9: "Oktober",
		10: "November",
		11: "Dezember",
	},
};

export const monthsShortName: { [key: string]: { [key: number]: string } } = {
	en: {
		0: "Jan",
		1: "Feb",
		2: "Mar",
		3: "Apr",
		4: "May",
		5: "Jun",
		6: "Jul",
		7: "Aug",
		8: "Sep",
		9: "Oct",
		10: "Nov",
		11: "Dec",
	},
	de: {
		0: "Jan",
		1: "Feb",
		2: "Mär",
		3: "Apr",
		4: "Mai",
		5: "Jun",
		6: "Jul",
		7: "Aug",
		8: "Sep",
		9: "Okt",
		10: "Nov",
		11: "Dez",
	},
};

/**
 * Create custom Matomo event. Taken from unmerged PR: https://github.com/kremalicious/gatsby-plugin-matomo/pull/46
 *
 * @see https://matomo.org/docs/event-tracking/
 */
export const trackCustomEvent = ({
	eventCategory = "engagement",
	eventAction = "click",
	eventName,
	eventValue,
}: MatomoEvent) => {
	if (process.env.NODE_ENV === "production" || window.dev === true) {
		if (!window._paq) return;

		const { _paq, dev } = window;

		_paq.push([
			"trackEvent",
			eventCategory,
			eventAction,
			eventName,
			eventValue,
		]);

		if (dev) {
			// eslint-disable-next-line no-console
			console.debug(
				`[Matomo] event tracked, category: ${eventCategory}, action: ${eventAction}, name: ${eventName}, value: ${eventValue}`,
			);
		}
	}
};

export const getEventForLink = (link: string): MatomoEventName | null => {
	if (link.startsWith("tel:")) {
		return "clickphone";
	} else if (link.startsWith("mailto:")) {
		return "clickmail";
	}

	return null;
};

/**
 * Adds a class, that will trigger a CSS animation, then removes it
 * after the animation has run (or after a specific timeout,
 * whichever comes first)
 *
 * @param className the className that triggers the animation
 * @param timeoutMs fallback timeout in ms
 */
export const useOneOffAnimationClass = <T extends HTMLElement>(
	className: string,
	timeoutMs = 2000,
): [RefObject<T>, VoidFunction, VoidFunction] => {
	const ref = useRef<T>(null);
	const [active, setActive] = useBoolean(false);

	// on trigger => add classname and track status
	const trigger = useCallback(() => {
		if (ref.current) {
			ref.current.classList.add(className);
			setActive(true);
		}
	}, [className, setActive]);

	// on cleanup => remove classname and update status
	const cleanUp = useCallback(() => {
		if (ref.current) {
			ref.current.classList.remove(className);
			setActive(false);
		}
	}, [className, setActive]);

	// whenever the animation is running, start a timeout…
	useEffect(() => {
		if (!active) {
			return () => {};
		}

		// … so either the timeout removes the classname again
		const timeout = setTimeout(cleanUp, timeoutMs);
		const el = ref.current;

		// … or the animationend event triggers the removal
		const onAnimationEnd = (ev: Event) => {
			ev.stopPropagation();
			cleanUp();
		};

		el?.addEventListener("animationend", onAnimationEnd);

		// on unmount => tidy up the timout and event listener
		return () => {
			clearTimeout(timeout);
			el?.removeEventListener("animationend", onAnimationEnd);
		};
	}, [active, cleanUp, timeoutMs]);

	return [ref, trigger, cleanUp];
};

/**
 * Constructs the full path for a link based on whether it's absolute or relative.
 *
 * @param to - The target path. Can be absolute (starting with "/") or relative.
 * @param locale - The site locale used for relative paths.
 * @returns The full path string.
 *
 * @example
 * ```typescript
 * // Absolute path
 * constructFullPath("/about", "en"); // "/about"
 *
 * // Relative path
 * constructFullPath("services", "en"); // "/en/services/"
 * ```
 */
export const constructFullPath = (to: string, locale: SiteLocale): string => {
	const cleanTo = stripStega(to);
	return cleanTo.startsWith("/") ? cleanTo : `/${locale}/${cleanTo}/`;
};

/**
 * Checks if a given link path matches the current pathname, indicating an active link state.
 *
 * @param to - The target path for the link. Can be undefined, absolute (starting with "/"), or relative.
 * @param pathname - The current pathname to compare against.
 * @param locale - The site locale used to construct the full path for relative links.
 * @returns `true` if the link is active (matches the current pathname), `false` otherwise.
 *
 * @example
 * ```typescript
 * // Absolute path
 * isLinkActive("/about", "/about", "en"); // true
 *
 * // Relative path
 * isLinkActive("services", "/en/services/", "en"); // true
 *
 * // Undefined path
 * isLinkActive(undefined, "/home", "en"); // false
 * ```
 */
export const isLinkActive = (
	to: string | undefined,
	pathname: string,
	locale: SiteLocale,
): boolean => {
	if (!to) return false;

	const fullPath = constructFullPath(to, locale);

	return pathname === fullPath;
};

/**
 * Determines if a navigation item is currently active based on the current pathname.
 *
 * This function checks if the current pathname matches either the main navigation item's route
 * or any of its child routes from menu columns. It constructs full paths for all possible
 * routes associated with the navigation item and checks for an exact match.
 *
 * @param item - The header navigation item to check for highlighting
 * @param pathname - The current page pathname to match against
 * @param locale - The site locale used to construct full paths
 * @param isDetailedCaseStudy - A flag indicating if the current page is a detailed case study page
 * @returns `true` if the navigation item is currently active, `false` otherwise
 */
export const isNavigationItemCurrent = (
	item: HeaderFieldsFragment["items"][number],
	pathname: string,
	locale: SiteLocale,
	isDetailedCaseStudy: boolean,
): boolean => {
	const childRouteNames = item.menuColumns
		.flatMap((column) => column.items)
		.map((child) => child.page?.routeName)
		.filter((routeName): routeName is string => Boolean(routeName));
	const allRouteNames = [
		...(item.page?.routeName ? [item.page.routeName] : []),
		...childRouteNames,
	];
	const fullPaths = allRouteNames.map((routeName) =>
		constructFullPath(routeName, locale),
	);

	return (
		fullPaths.includes(pathname) ||
		(item.id === SUCCESS_STORIES_HEADER_ITEM_ID[locale] &&
			isDetailedCaseStudy) ||
		(item.id === BLOG_HEADER_ITEM_ID[locale] &&
			pathname.startsWith(`/${locale}/blog/`))
	);
};
